import type { InitDateGroups, NormalizedInitDatesByModelResponse } from "@/api/hooks/micro-service/EnergyForecast";
import { getEnergyPlotModelColor } from "@/constants/create";
import type { AvailableValueNode } from "@/overlay/components/LayerStackWindow/EnergyPlotSettings/useAvailableEnergyForecastDataTree";
import type { GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import { DateTime } from "luxon";
export { DEFAULT_ENS_MEMBERS, ECMWF_ENS_MODEL_ID } from "../const";
import {
  type EnergyPlot,
  type EnergyPlotGroup,
  createPreviousEnergyPlotRun,
  normalizeEnergyPlotGroupRuns,
  resolveLatestEnergyInitDate,
  resolveLatestEnergyInitDates,
} from "../types";

export type EnergyPlotModelSelectorItem = {
  id: string;
  name: string;
  enabled: boolean;
  color: string;
};

export type SelectedDay = "today" | "yesterday";

export type SelectedRunInitDatesByModel = Record<string, number[]>;

export type RunScrollerState = {
  canScrollLeft: boolean;
  canScrollRight: boolean;
};

type DayOption = {
  value: SelectedDay;
  label: string;
};

const SUMMARY_PILL_GAP_PX = 4;

export const RUN_SCROLLER_SCROLL_STEP_PX = 160;

/**
 * Converts a model node from the availability tree into the selector shape.
 * Empty tree nodes are ignored because they cannot map back to an API model id.
 */
export function transformNodeToModelItem(node: AvailableValueNode): EnergyPlotModelSelectorItem | null {
  if (!node.value) {
    return null;
  }

  return {
    id: node.value,
    name: node.value,
    enabled: false,
    color: getEnergyPlotModelColor(node.value),
  };
}

/**
 * Merges currently selected groups with available model ids.
 * Available models keep their source order, while selected models missing from
 * availability are appended so they remain visible.
 */
export function resolveModelItems(
  availableModels: EnergyPlotModelSelectorItem[],
  groups: EnergyPlot["groups"],
): {
  modelItems: EnergyPlotModelSelectorItem[];
  selectedModels: EnergyPlotModelSelectorItem[];
} {
  const availableModelsById = new Map(availableModels.map((model) => [model.id, model] as const));
  const selectedModelsById = new Map<string, EnergyPlotModelSelectorItem>();

  for (const group of groups) {
    if (!group.model || selectedModelsById.has(group.model)) {
      continue;
    }

    const availableModel = availableModelsById.get(group.model);

    selectedModelsById.set(group.model, {
      id: group.model,
      name: availableModel?.name ?? group.model,
      enabled: true,
      color: group.plotSeriesStyles.power?.color ?? getEnergyPlotModelColor(group.model),
    });
  }

  const selectedModels = Array.from(selectedModelsById.values());
  const modelItems = [
    ...availableModels.map((model) => selectedModelsById.get(model.id) ?? model),
    ...selectedModels.filter((model) => !availableModelsById.has(model.id)),
  ];
  return { modelItems, selectedModels };
}

/**
 * Resolves selector items for a forecast type and optional site.
 * If the selected site has no model-specific availability, the full forecast
 * type model list is used as a fallback.
 */
export function resolveEnergyPlotModelItems(
  tree: AvailableValueNode | undefined,
  forecastType: string,
  siteId: string | undefined,
  groups: EnergyPlot["groups"],
) {
  const forecastTypeNode = tree?.findChildrenNode(forecastType);
  const modelNodes = forecastTypeNode?.children || [];
  const siteFilteredModelNodes = siteId
    ? modelNodes.filter((modelNode) => (modelNode.children || []).some((siteNode) => siteNode.value === siteId))
    : modelNodes;
  const availableModelNodes = siteFilteredModelNodes.length > 0 ? siteFilteredModelNodes : modelNodes;
  const availableModelIds = availableModelNodes.flatMap((node) => (node.value ? [node.value] : []));
  const availableModels = availableModelNodes.flatMap((node) => {
    const model = transformNodeToModelItem(node);
    return model ? [model] : [];
  });

  return { ...resolveModelItems(availableModels, groups), availableModelIds };
}

/** Returns whether a group already carries an explicit run selection. */
export function hasStoredRunState(group: EnergyPlotGroup): boolean {
  return group.actualRun !== undefined || (group.previousRuns?.length ?? 0) > 0;
}

/**
 * Creates the initial deterministic run selection for a model group.
 * The latest available init date becomes `actualRun`; the next latest run is
 * stored as a previous run when available.
 */
export function createDefaultRunSelection(
  group: EnergyPlotGroup,
  initDateGroups: InitDateGroups | undefined,
): Pick<EnergyPlotGroup, "actualRun" | "previousRuns"> {
  const [latestInitDate, ...previousInitDates] = resolveLatestInitDates(initDateGroups, 2);

  return {
    ...(latestInitDate !== undefined
      ? {
          actualRun: {
            initDate: latestInitDate,
            plotSeriesStyles: group.plotSeriesStyles,
          },
        }
      : {}),
    previousRuns: previousInitDates.map((initDate, index) => createPreviousEnergyPlotRun(group, initDate, index)),
  };
}

/** Deduplicates finite init dates and sorts them oldest to newest. */
function normalizeInitDates(initDates: Array<number | null | undefined>): number[] {
  return Array.from(new Set(initDates.filter((initDate): initDate is number => Number.isFinite(initDate)))).sort(
    (a, b) => a - b,
  );
}

/**
 * Builds the selected run lookup consumed by menu rows.
 * Multiple groups for the same model are merged to support legacy duplicated
 * groups while the selector presents a single model row.
 */
export function resolveSelectedRunInitDatesByModel(groups: EnergyPlot["groups"]): SelectedRunInitDatesByModel {
  return groups.reduce<SelectedRunInitDatesByModel>((acc, group) => {
    if (!group.model) return acc;

    const normalizedGroup = normalizeEnergyPlotGroupRuns(group);
    const selectedRunInitDates = normalizeInitDates([
      normalizedGroup.actualRun?.initDate,
      ...(normalizedGroup.previousRuns ?? []).map((run) => run.initDate),
    ]);

    if (selectedRunInitDates.length === 0) {
      return acc;
    }

    const current = acc[group.model] ?? [];
    const merged = [...current, ...selectedRunInitDates];
    acc[group.model] = normalizeInitDates(merged);

    return acc;
  }, {});
}

/** Formats an init-date timestamp as a compact run time in the selected timezone. */
export function formatRunInitDate(initDate: number, timezone: GuiTimeZone) {
  return DateTime.fromSeconds(initDate, { zone: timezone }).toFormat("HH:mm");
}

/** Formats an init-date timestamp for labels that need both date and run time. */
export function formatRunInitDateFull(initDate: number, timezone: GuiTimeZone) {
  return DateTime.fromSeconds(initDate, { zone: timezone }).toFormat("yyyy-MM-dd HH:mm");
}

/** Formats the day label used by the run day switch. */
export function formatRunDayLabel(initDate: number, timezone: GuiTimeZone) {
  return DateTime.fromSeconds(initDate, { zone: timezone }).toFormat("yyyy-MM-dd");
}

/** Creates the stable map key for a model's run scroller on a specific day. */
export function getRunScrollerKey(modelId: string, day: SelectedDay) {
  return `${modelId}:${day}`;
}

/**
 * Resolves which run chips should appear selected in the menu.
 * Enabled deterministic models always include the latest available run, while
 * stale selected init dates are dropped when they are no longer available.
 */
export function resolveSelectedRunInitDates(
  initDates: number[],
  selectedRunInitDates: number[] | undefined,
  isModelEnabled: boolean,
  defaultSelectedRunInitDates: number[],
) {
  if (!isModelEnabled) {
    return [];
  }

  const selectedInitDates =
    selectedRunInitDates && selectedRunInitDates.length > 0
      ? ensureLatestSelectedRunInitDates(selectedRunInitDates, defaultSelectedRunInitDates[0])
      : defaultSelectedRunInitDates;

  return normalizeInitDates(selectedInitDates).filter((initDate) => initDates.includes(initDate));
}

/** Returns the latest available init date across flat or grouped availability. */
export function resolveLatestInitDate(initDateGroups: InitDateGroups | undefined): number | undefined {
  return resolveLatestEnergyInitDate(initDateGroups);
}

/** Returns the latest available init dates newest first. */
export function resolveLatestInitDates(initDateGroups: InitDateGroups | undefined, count: number): number[] {
  return resolveLatestEnergyInitDates(initDateGroups, count);
}

/**
 * Ensures the current latest run remains selected even after users remove older
 * previous runs from the selector.
 */
export function ensureLatestSelectedRunInitDates(
  selectedRunInitDates: number[],
  latestInitDate: number | undefined,
): number[] {
  if (latestInitDate === undefined) {
    return normalizeInitDates(selectedRunInitDates);
  }

  return normalizeInitDates([...selectedRunInitDates, latestInitDate]);
}

/**
 * Calculates how many selected model pills fit in the trigger summary.
 * The overflow badge is not allowed to displace a pill that still fits.
 */
export function resolveVisibleModelCount(modelWidths: number[], availableWidth: number): number {
  const totalModels = modelWidths.length;

  if (totalModels === 0) {
    return 0;
  }

  if (availableWidth <= 0) {
    return totalModels;
  }

  let usedWidth = 0;

  for (let index = 0; index < totalModels; index += 1) {
    const gapWidth = index > 0 ? SUMMARY_PILL_GAP_PX : 0;
    const nextWidth = usedWidth + gapWidth + modelWidths[index];

    if (nextWidth > availableWidth) {
      return index;
    }

    usedWidth = nextWidth;
  }

  return totalModels;
}

/**
 * Finds a representative init date for a day bucket so the day switch can show
 * the actual calendar date instead of a static "today/yesterday" label.
 */
function resolveFirstInitDateForDay(
  initDatesByModel: NormalizedInitDatesByModelResponse | undefined,
  day: SelectedDay,
): number {
  if (!initDatesByModel) {
    return 0;
  }

  for (const initDateGroups of Object.values(initDatesByModel)) {
    const firstInitDate = initDateGroups[day][0];
    if (firstInitDate !== undefined) {
      return firstInitDate;
    }
  }

  return 0;
}

/** Builds the two day-switch options from normalized init-date availability. */
export function resolveDaySwitchOptions(
  initDatesByModel: NormalizedInitDatesByModelResponse | undefined,
  timezone: GuiTimeZone,
): DayOption[] {
  return [
    {
      value: "yesterday",
      label: formatRunDayLabel(resolveFirstInitDateForDay(initDatesByModel, "yesterday"), timezone),
    },
    {
      value: "today",
      label: formatRunDayLabel(resolveFirstInitDateForDay(initDatesByModel, "today"), timezone),
    },
  ];
}
